import React, { useState, useEffect } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  Background,
  Node,
  Edge,
  MarkerType,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Card, CardContent, Typography, Box, Chip, Divider, Button, useTheme } from '@mui/material';
import { ChemicalRoute } from '../../types';

interface ChemicalFlowDiagramProps {
  route: ChemicalRoute;
}

const ChemicalFlowDiagramInner: React.FC<ChemicalFlowDiagramProps> = ({ route }) => {
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [expanded, setExpanded] = useState(false);
  const isLongFlow = route.processSteps.length > 5;

  const rfInstance = useReactFlow();

  useEffect(() => {
    const timer = setTimeout(() => {
      rfInstance.fitView({ padding: 0.15, duration: 300 });
    }, 60);
    return () => clearTimeout(timer);
  }, [route, rfInstance, expanded]);

  useEffect(() => {
    setExpanded(false);
  }, [route.id]);

  useEffect(() => {
    const nodeWidth = 180;
    const verticalSpacing = 90;
    const horizontalSpacing = 240;
    const startX = 0;
    const centerY = 150;

    // 按首个投料工序分组：原料节点就近摆放在目标工序的左侧一列，体现分阶段投料位置
    const groupByStep = new Map<number, { name: string; index: number }[]>();
    route.feedstock.forEach((item, index) => {
      const entry = route.feedstockEntry?.[item];
      const primary = entry && entry.length > 0 ? Math.min(...entry) : 0;
      if (!groupByStep.has(primary)) groupByStep.set(primary, []);
      groupByStep.get(primary)!.push({ name: item, index });
    });

    const feedstockNodes: Node[] = [];
    groupByStep.forEach((items, stepIdx) => {
      items.forEach((it, j) => {
        // 首道工序的原料以主线为中心对称排列；其余组的原料排在工序线下方，
        // 避免与前一工序节点（同列的 centerY 处）重叠
        const yOffset =
          stepIdx === 0
            ? (j - (items.length - 1) / 2) * verticalSpacing
            : 70 + j * verticalSpacing;
        feedstockNodes.push({
          id: `feedstock-${it.index}`,
          type: 'input',
          position: { x: startX + stepIdx * horizontalSpacing, y: centerY + yOffset },
          sourcePosition: Position.Right,
          data: { label: it.name },
          style: {
            background: isDarkMode ? '#1e3a5f' : '#e3f2fd',
            border: `2px solid ${isDarkMode ? '#4fc3f7' : '#2196f3'}`,
            borderRadius: '8px',
            padding: 10,
            fontSize: 14,
            fontWeight: 600,
            color: isDarkMode ? '#ffffff' : '#000000',
          },
        });
      });
    });

    const processNodes: Node[] = route.processSteps.map((step, index) => ({
      id: `step-${index}`,
      position: { x: startX + (index + 1) * horizontalSpacing, y: centerY },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      data: { label: step },
      style: {
        background: activeStep === index 
          ? (isDarkMode ? '#4a3728' : '#fff3e0')
          : (isDarkMode ? '#2a2a2a' : '#f5f5f5'),
        border: activeStep === index 
          ? `3px solid ${isDarkMode ? '#ffb74d' : '#ff9800'}`
          : `2px solid ${isDarkMode ? '#666666' : '#9e9e9e'}`,
        borderRadius: '8px',
        padding: 15,
        fontSize: 13,
        fontWeight: 500,
        width: nodeWidth,
        cursor: 'pointer',
        color: isDarkMode ? '#ffffff' : '#000000',
      },
    }));

    const productNodes: Node[] = route.products.map((item, index) => {
      const yOffset = (index - (route.products.length - 1) / 2) * verticalSpacing;
      return {
        id: `product-${index}`,
        type: 'output',
        position: { x: startX + (route.processSteps.length + 1) * horizontalSpacing, y: centerY + yOffset },
        targetPosition: Position.Left,
        data: { label: item },
        style: {
          background: isDarkMode ? '#1b5e20' : '#e8f5e9',
          border: `2px solid ${isDarkMode ? '#66bb6a' : '#4caf50'}`,
          borderRadius: '8px',
          padding: 10,
          fontSize: 14,
          fontWeight: 600,
          color: isDarkMode ? '#ffffff' : '#000000',
        },
      };
    });

    setNodes([...feedstockNodes, ...processNodes, ...productNodes]);

    const edgeList: Edge[] = [];

    route.feedstock.forEach((item, index) => {
      const entrySteps = route.feedstockEntry?.[item] ?? [0];
      entrySteps.forEach((stepIdx) => {
        edgeList.push({
          id: `feedstock-${index}-step-${stepIdx}`,
          source: `feedstock-${index}`,
          target: `step-${stepIdx}`,
          animated: true,
          style: { stroke: isDarkMode ? '#4fc3f7' : '#2196f3', strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: isDarkMode ? '#4fc3f7' : '#2196f3' },
        });
      });
    });

    route.processSteps.forEach((_, index) => {
      if (index < route.processSteps.length - 1) {
        edgeList.push({
          id: `step-${index}-step-${index + 1}`,
          source: `step-${index}`,
          target: `step-${index + 1}`,
          animated: true,
          style: { stroke: isDarkMode ? '#666666' : '#9e9e9e', strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: isDarkMode ? '#666666' : '#9e9e9e' },
        });
      }
    });

    route.products.forEach((_, index) => {
      edgeList.push({
        id: `step-last-product-${index}`,
        source: `step-${route.processSteps.length - 1}`,
        target: `product-${index}`,
        animated: true,
        style: { stroke: isDarkMode ? '#66bb6a' : '#4caf50', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: isDarkMode ? '#66bb6a' : '#4caf50' },
      });
    });

    setEdges(edgeList);
  }, [route, activeStep, setNodes, setEdges, isDarkMode]);

  const onNodeClick = (_event: React.MouseEvent, node: Node) => {
    if (node.id.startsWith('step-')) {
      const stepIndex = parseInt(node.id.split('-')[1], 10);
      setActiveStep(activeStep === stepIndex ? null : stepIndex);
    }
  };

  return (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
    }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            color: isDarkMode ? '#66bb6a' : '#2e7d32', 
            fontWeight: 600 
          }}
        >
          {route.name} 工艺流程
        </Typography>
        
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">原料</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
              {route.feedstock.map((item) => (
                <Chip key={item} label={item} size="small" color="primary" variant="outlined" />
              ))}
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">产品</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
              {route.products.map((item) => (
                <Chip key={item} label={item} size="small" color="success" variant="outlined" />
              ))}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 2, borderColor: isDarkMode ? '#444444' : '#e0e0e0' }} />

        {isLongFlow && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              流程较长，已折叠为概览显示
            </Typography>
            <Button size="small" variant="outlined" onClick={() => setExpanded(!expanded)}>
              {expanded ? '收起' : '展开大图'}
            </Button>
          </Box>
        )}
        <Box sx={{ 
          height: isLongFlow ? (expanded ? 620 : 360) : 340, 
          border: '1px solid', 
          borderColor: isDarkMode ? '#444444' : '#e0e0e0',
          borderRadius: 2 
        }}>
          <ReactFlow
            key={route.id}
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            fitView
            minZoom={0.1}
            attributionPosition="bottom-left"
          >
            <Background 
              variant={BackgroundVariant.Dots} 
              gap={12} 
              size={1}
              color={isDarkMode ? '#444444' : '#e0e0e0'}
            />
          </ReactFlow>
        </Box>

        {activeStep !== null && (
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            backgroundColor: isDarkMode ? '#4a3728' : '#fff3e0', 
            borderRadius: 2 
          }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600, 
                color: isDarkMode ? '#ffb74d' : '#ff9800' 
              }}
            >
              当前步骤: {route.processSteps[activeStep]}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              步骤 {activeStep + 1} / {route.processSteps.length}
            </Typography>
          </Box>
        )}

        {route.advantages && route.advantages.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              技术优势:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {route.advantages.map((adv, index) => (
                <Chip
                  key={index}
                  label={adv}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {route.challenges && route.challenges.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              技术挑战:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {route.challenges.map((chal, index) => (
                <Chip
                  key={index}
                  label={chal}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const ChemicalFlowDiagram: React.FC<ChemicalFlowDiagramProps> = (props) => (
  <ReactFlowProvider>
    <ChemicalFlowDiagramInner {...props} />
  </ReactFlowProvider>
);

export default ChemicalFlowDiagram;
